import { useState, useEffect, useMemo } from "react";

import BuildingProperties from "./object-properties/BuildingProperties";
import AssetProperties from "./object-properties/AssetProperties";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { userAPI } from "./UserAPI";
import { useTranslation } from 'react-i18next';
import LegalPersonProperties from "./object-properties/LegalPersonProperties";
import FloorProperties from "./object-properties/FloorProperties";
import SpaceProperties from "./object-properties/SpaceProperties";
import ContentPanel from "./components/ContentPanel";
import BridgeProperties from "./object-properties/BridgeProperties";
import { getEntityType, Parcel } from "@the-real-insight/tri-model";
import { ApplicationError } from "./general/Errors";
import ParcelProperties from "./object-properties/ParcelProperties";
import GenericEntityView from "./entities/GenericEntityView";
import { knownEntityType } from "./entities/entityTypes";

import { Loader2 } from "lucide-react";
import type { AppShellContext } from "./components/AppShell";
export default function EntityPage() {
    const { t } = useTranslation();
    const { search } = useLocation();
    const navigate = useNavigate();
    const [entity, setEntity] = useState();
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const searchParams = useMemo(() => new URLSearchParams(search), [search]);

    // Zwei Adressen für dieselbe Seite: die RESTful `/entities/:type/:id` und die ältere mit Query.
    const params = useParams();
    const entityType = params.entityType ?? searchParams.get("selectedEntityType");
    const entityId = params.entityId ?? searchParams.get("selectedEntity");

    useEffect(() => {
        const call = async () => {
            if (!entityType) {
                navigate('/chatbot');
            } else if (!knownEntityType(entityType)) {
                setLoadError(t('portfolioDashboard.entity.unknownType', { type: entityType }));
                setLoading(false);
            } else if (entityId) {
                console.log(`Retrieving ${entityType} with ID ${entityId}`);

                setLoading(true);
                setLoadError(null);
                try {
                    // @ts-ignore
                    const newEntity = await userAPI.getEntityByID(entityType, entityId);

                    console.log('Entity found >>>', newEntity);

                    setEntity(newEntity);
                } catch (error) {
                    const message =
                        error instanceof ApplicationError
                            ? error.message
                            : error instanceof Error
                              ? error.message
                              : 'Unable to load entity';
                    setLoadError(message);
                    setEntity(undefined);
                } finally {
                    setLoading(false);
                }
            }
        };

        void call();
    }, [entityId, entityType, navigate, t]);

    const { setTitle } = useOutletContext<AppShellContext>();

    const getTitle = () => {
        if (!entity || !entityType) {
            return '';
        }

        switch (entityType) {
            case 'Parcel':
                return (entity as Parcel).getFullName();
            case 'Space':
                return (entity as { name?: string }).name || '';
            default:
                return getEntityType(entityType)?.type.label.de || '';
        }
    }
    
    const title = getTitle();

    useEffect(() => {
        setTitle(title);
    }, [title, setTitle]);

    return <>
        {loading
            ?
            <div className="flex flex-1 items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
                <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin" />
                {`${t('global.loading')} ...`}
            </div>
            :
            loadError
            ?
            <ContentPanel backgroundColor="transparent">
                <div role="alert" className="mt-4 text-sm text-destructive">{loadError}</div>
            </ContentPanel>
            :
            <ContentPanel backgroundColor="transparent">
                <div className="entityFillColumn">
                    {(() => {
                        // @ts-ignore
                        switch (entityType) {
                            case 'Asset':
                                return <AssetProperties asset={entity}></AssetProperties>;
                            case 'Floor':
                                return <FloorProperties floor={entity}></FloorProperties>;
                            case 'Space':
                                return <SpaceProperties space={entity}></SpaceProperties>;
                            case 'Building':
                                return <BuildingProperties building={entity}></BuildingProperties>;
                            case 'LegalPerson':
                                return <LegalPersonProperties legalPerson={entity}></LegalPersonProperties>;
                            case 'Bridge':
                                return <BridgeProperties bridge={entity}></BridgeProperties>;
                            case 'Parcel':
                                return <ParcelProperties parcel={entity}></ParcelProperties>;
                            default:
                                // Jeder andere Typ: aus dem Schema angezeigt, zunächst ohne Bearbeiten.
                                return <GenericEntityView entityType={entityType as string} entity={entity}></GenericEntityView>;
                        }
                    })()}
                </div>
            </ContentPanel>
        }
    </>;
}
